import React, { useEffect, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import gsap from 'gsap'
import { Draggable } from 'gsap/all'
import { InertiaPlugin } from '@utils/gsap/InertiaPlugin'
import {
  CaseStudiesWrapper,
  CaseStudiesCarousel,
  CaseStudiesHeader,
  CaseStudiesMain,
  CaseStudyMain,
  CaseStudiesNav,
  CaseStudiesNavItem,
  CaseStudiesTitle,
  CaseStudiesCardWrapper,
  CaseStudyContainer,
  CaseStudyContent,
  CaseStudyImage,
  CaseStudiesFooter
} from './index.style'
import Container from '@components/Container'
import Spacer from '@components/Spacer'
import Button from '@components/Button'
import PageTransitionLink from '@components/PageTransitionLink';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Caption, Heading4Serif } from '@components/TextStyles';
import RichText from '@components/RichText';
import CMSButton from '@components/CMSButton'
import IconArrowRight from '@svgs/IconArrowRight'

const CaseStudies = ({ data, caseStudies: items, backgroundColor, textColor }) => {
  const { title } = data;
  const [activeIndex, setActiveIndex] = useState(0)
  const [carouselAtEnd, setCarouselAtEnd] = useState(false)
  const activeIndexRef = useRef(activeIndex)
  const $caseStudiesWrapper = useRef()
  const $caseStudies = useRef()
  const $singleCard = useRef()
  const slideWidth = useRef()
  const link = {
    text: "View more work",
    url: "/our-work"
  }

  const getSlideWidth = useCallback(() => {
    if (!$singleCard.current) return 0; // Early return if ref is null
    
    const cardWidth = parseFloat(
      window.getComputedStyle($singleCard.current).width.replace(/px$/, '')
    )
    const cardMargin = parseFloat(
      window.getComputedStyle($singleCard.current).marginLeft.replace(/px$/, '')
    )
    
    return cardWidth + cardMargin;
}, []);


  const getMaxWidth = () =>
    -$caseStudies.current.offsetWidth + $caseStudiesWrapper.current.offsetWidth

  const getCarouselX = useCallback(targetX => {
    const maxWidth = getMaxWidth()
    const isEnd = targetX < maxWidth
    const isStart = targetX > 0
    const x = isEnd ? maxWidth : isStart ? 0 : targetX

    setCarouselAtEnd(isEnd)
    return x
  }, [])

  const goToCard = useCallback(
    index => {
      gsap.to($caseStudies.current, {
        x: getCarouselX(-(slideWidth.current * index)),
        ease: 'inOut',
        duration: 0.3,
        onComplete: () => {
          setActiveIndex(index)
        },
      })
    },
    [getCarouselX]
  )

  useEffect(() => {
    gsap.registerPlugin(Draggable, InertiaPlugin)

    Draggable.create($caseStudies.current, {
      type: 'x',
      edgeResistance: 1,
      inertia: true,
      snap: function (endValue) {
        const maxWidth = getMaxWidth()
        const clampedValue = gsap.utils.clamp(maxWidth, 0, endValue)

        setActiveIndex(Math.ceil(Math.abs(clampedValue / slideWidth.current)))
        setCarouselAtEnd(clampedValue <= maxWidth)
        return clampedValue
      },
    })

    const applyDraggableBounds = () => {
      slideWidth.current = getSlideWidth()

      Draggable.get($caseStudies.current).applyBounds({
        minX: $caseStudiesWrapper.current.offsetWidth - $caseStudies.current.offsetWidth,
        maxX: 0,
      })

      goToCard(activeIndexRef.current)
    }

    applyDraggableBounds()

    window.addEventListener('resize', applyDraggableBounds)

    return () => {
      window.removeEventListener('resize', applyDraggableBounds)
    }
  }, [getSlideWidth, goToCard])

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  const getSlidePaginationAmount = () => {
    if (window.innerWidth > 2060) {
      return 3
    } else if (window.innerWidth > 1520) {
      return 2
    } else if (window.innerWidth > 990) {
      return 1
    } else if (window.innerWidth > 590) {
      return 1
    } else {
      return 1
    }
  }

  const goToPrevious = () => {
    goToCard(Math.max(activeIndex - getSlidePaginationAmount(), 0))
  }

  const goToNext = () => {
    goToCard(
      Math.min(activeIndex + getSlidePaginationAmount(), items.length - 1)
    )
  }

  return (
    <>
      {(items.length > 0) && 
        <CaseStudiesMain>
          <Container>
            <CaseStudiesWrapper ref={$caseStudiesWrapper}>
            <Spacer size={[60, 115]} />
              <CaseStudiesHeader>
                <CaseStudiesTitle>{title}</CaseStudiesTitle>
                <CaseStudiesNav backgroundColor={backgroundColor} fontColor={textColor}>
                  <CaseStudiesNavItem>
                    <Button
                      variant="outline"
                      onClick={goToPrevious}
                      size="large"
                      color="white"
                      disabled={activeIndex === 0}
                    >
                      PREVIOUS
                    </Button>
                  </CaseStudiesNavItem>
                  <CaseStudiesNavItem>
                    <Button
                      variant="outline"
                      onClick={goToNext}
                      size="large"
                      color="white"
                      disabled={carouselAtEnd}
                    >
                      NEXT
                    </Button>
                  </CaseStudiesNavItem>
                </CaseStudiesNav>
              </CaseStudiesHeader>
              <Spacer size={[30, 70]} />

              <CaseStudiesCarousel ref={$caseStudies} centered={(activeIndex === 0 && carouselAtEnd)}>
                {React.Children.toArray(
                  items.map((item, itemIndex) => (
                    <CaseStudiesCardWrapper
                      ref={itemIndex === 1 ? $singleCard : undefined}
                    >
                      <CaseStudyMain>
                            <PageTransitionLink to={`our-work/${item.slug}`} className="caseStudyImageLink">
                                <CaseStudyContainer className="caseStudyContainer">
                                    <CaseStudyContent className='content'>
                                        <Heading4Serif as="h2" className="dOnly"><strong>{item.title}</strong></Heading4Serif>
                                        <RichText
                                            className="dOnly"
                                            content={item.heroText}
                                            paragraphSize="heading4Serif"
                                            as="h3"
                                            />
                                        {(item.metadata.tags.length > 0) && 
                                          <>
                                            <Spacer size={[20, 40]} />
                                            <Caption>
                                            {(item.metadata.tags[0].name.split(': ').length > 1) ? item.metadata.tags[0].name.split(': ')[1]: item.metadata.tags[0].name}
                                            </Caption>
                                          </>
                                        }
                                    </CaseStudyContent>
                                    <CaseStudyImage className="caseStudyImage">
                                        <GatsbyImage
                                            image={item.mainImage ? item.mainImage.gatsbyImageData : item.secondaryImage.gatsbyImageData}
                                            alt={item.mainImage ? item.mainImage.description : item.secondaryImage.description}
                                            height={738}
                                            width={738}
                                        />
                                    </CaseStudyImage>
                                </CaseStudyContainer>
                            </PageTransitionLink>
                        </CaseStudyMain>
                        <RichText
                          className="mOnly"
                          content={item.heroText}
                          paragraphSize="small"
                        />
                    </CaseStudiesCardWrapper>
                  ))
                )}
              </CaseStudiesCarousel>

              <Spacer size={[40, 92]} />

              <CaseStudiesFooter>
                {link && (
                  <>
                    <CMSButton
                      link={link}
                      variant="text"
                      iconRight={<IconArrowRight />}
                    />
                  </>
                )}
              </CaseStudiesFooter>
            </CaseStudiesWrapper>
            <Spacer size={[30, 70]} />
          </Container>
        </CaseStudiesMain>
      }
    </>
  )
}

CaseStudies.propTypes = {
  items: PropTypes.array,
}

export default CaseStudies
